import { memo } from "react";

import BronzeTierLarge from "@zenith/assets/icons/BronzeTierLarge";
import SilverTierLarge from "@zenith/assets/icons/SilverTierLarge";
import GoldTierLarge from "@zenith/assets/icons/GoldTierLarge";
import PlatinumTierLarge from "@zenith/assets/icons/PlatinumTierLarge";
import { OkTick } from "@zenith/assets/icons/components";
import {
  LimitsInfoLimitDTO,
  StartUpgradeTierRequestDTO,
  StartUpgradeTierResponseDTO,
  TierDetailDTO,
  UpgradeProcessInfoDTO,
} from "@zenith/services/user";
import Clock from "@zenith/assets/icons/Clock";
import {
  DEFAULT_CURRENCY_SYMBOL,
  UNLIMITED,
  UPGRADE_PER_TRANSACTION,
} from "@zenith/data/constants";
import { getSuffixedNumber } from "@zenith/utils";
import EditIcon from "@zenith/assets/icons/EditIcon";
import ButtonComponent from "../../Button/button-component";

/**
 * Props for Card Tier Component.
 * @interface
 * @name CardTierComponentProps
 * @kind interface
 * @exports
 */
export interface CardTierComponentProps {
  /**
   * The currency.
   * @property
   * @name currency
   * @kind property
   * @memberof CardTierComponentProps
   * @type {string}
   */
  currency: string;
  /**
   * The tier needed for the Card Tier Component.
   * @property
   * @name tier
   * @kind property
   * @memberof CardTierComponentProps
   * @type {StartUpgradeTierRequestDTO.tierType | TierDetailDTO.tierName}
   */
  tier: StartUpgradeTierRequestDTO.tierType | TierDetailDTO.tierName;
  /**
   * The Card Tier Component subtitle.
   * @property
   * @name subTitle
   * @kind property
   * @memberof CardTierComponentProps
   * @type {string}
   */
  subTitle: string;
  /**
   * The Card Tier Component description.
   * @property
   * @name description
   * @kind property
   * @memberof CardTierComponentProps
   * @type {string}
   */
  description: string;
  /**
   * Standard DOM Attribute Mouse Event.
   * @method
   * @name onClick
   * @kind property
   * @memberof CardTierComponentProps
   * @returns {void}
   */
  onClick: () => void;
  /**
   * If `true`, the component is selected.
   * @property
   * @name selected
   * @kind property
   * @memberof CardTierComponentProps
   * @type {boolean}
   */
  selected: boolean;
  /**
   * If `true`, disables the Upgrade.
   * @property
   * @name disableUpgrade
   * @kind property
   * @memberof CardTierComponentProps
   * @type {boolean}
   */
  disableUpgrade: boolean;
  /**
   * The upgrade Process Status.
   * @property
   * @name upgradeProcessStatus
   * @kind property
   * @memberof CardTierComponentProps
   * @type {StartUpgradeTierResponseDTO.generalProcessStatus | UpgradeProcessInfoDTO.generalStatus | undefined}
   */
  upgradeProcessStatus:
    | StartUpgradeTierResponseDTO.generalProcessStatus
    | UpgradeProcessInfoDTO.generalStatus
    | undefined;
  /**
   * The upgrade Process Tier Name.
   * @property
   * @name upgradeProcessTierName
   * @kind property
   * @memberof CardTierComponentProps
   * @type {StartUpgradeTierResponseDTO.tierType | UpgradeProcessInfoDTO.tierName | undefined}
   */
  upgradeProcessTierName:
    | StartUpgradeTierResponseDTO.tierType
    | UpgradeProcessInfoDTO.tierName
    | undefined;
  /**
   * If `true`, the Current Tier is lower than the Target Tier.
   * @property
   * @name isTierLowerThanTargetTier
   * @kind property
   * @memberof CardTierComponentProps
   * @type {boolean}
   */
  isTierLowerThanTargetTier: boolean;
  /**
   * The wallet Balance Limit.
   * @property
   * @name walletBalanceLimit
   * @kind property
   * @memberof CardTierComponentProps
   * @type {string | LimitsInfoLimitDTO}
   */
  walletBalanceLimit: LimitsInfoLimitDTO | string;
  /**
   * The credit Transaction Limit.
   * @property
   * @name creditTransactionLimit
   * @kind property
   * @memberof CardTierComponentProps
   * @type {string | LimitsInfoLimitDTO[]}
   */
  creditTransactionLimit: LimitsInfoLimitDTO[] | string;
  /**
   * The debit Transaction Limit.
   * @property
   * @name debitTransactionLimit
   * @kind property
   * @memberof CardTierComponentProps
   * @type {string | LimitsInfoLimitDTO[]}
   */
  debitTransactionLimit: LimitsInfoLimitDTO[] | string;
  /**
   * The tier name.
   * @property
   * @name tierName
   * @kind property
   * @memberof CardTierComponentProps
   * @type {StartUpgradeTierRequestDTO.tierType | TierDetailDTO.tierName | undefined}
   */
  tierName:
    | StartUpgradeTierRequestDTO.tierType
    | TierDetailDTO.tierName
    | undefined;
}

/**
 * Gets the tier icon.
 * @function
 * @name getTierIcon
 * @kind function
 * @param {StartUpgradeTierRequestDTO.tierType | TierDetailDTO.tierName} tier The tier.
 * @returns {JSX.Element} The tier icon.
 */
function getTierIcon(
  tier: StartUpgradeTierRequestDTO.tierType | TierDetailDTO.tierName
): JSX.Element {
  switch (tier) {
    case StartUpgradeTierRequestDTO.tierType.BRONZE:
    case TierDetailDTO.tierName.BRONZE:
      return <BronzeTierLarge />;
    case StartUpgradeTierRequestDTO.tierType.SILVER:
    case TierDetailDTO.tierName.SILVER:
      return <SilverTierLarge />;
    case StartUpgradeTierRequestDTO.tierType.GOLD:
    case TierDetailDTO.tierName.GOLD:
      return <GoldTierLarge />;
    case StartUpgradeTierRequestDTO.tierType.PLATINUM:
    case TierDetailDTO.tierName.PLATINUM:
      return <PlatinumTierLarge />;
    default:
      throw new Error("Tier not mapped.");
  }
}

/**
 * Memoized Card Tier Component.
 * @constant
 * @name CardTierComponent
 * @kind variable
 * @type {React.NamedExoticComponent<CardTierComponentProps>}
 */
const CardTierComponent: React.NamedExoticComponent<CardTierComponentProps> =
  memo(
    /**
     * Card Tier Component.
     * @function
     * @name CardTierComponent
     * @kind function
     * @memberof CardTierComponent
     * @param {CardTierComponentProps} props Props for Card Tier Component.
     * @returns {JSX.Element} The Card Tier Component.
     */
    function CardTierComponent(props: CardTierComponentProps): JSX.Element {
      const {
        description,
        disableUpgrade,
        onClick,
        selected,
        subTitle,
        tier,
        upgradeProcessStatus,
        upgradeProcessTierName,
        isTierLowerThanTargetTier,
      } = props;

      const creditLimitArray = Array.isArray(props.creditTransactionLimit)
        ? props.creditTransactionLimit[0]
        : null;
      const debitLimitArray = Array.isArray(props.debitTransactionLimit)
        ? props.debitTransactionLimit[0]
        : null;

      const creditLimitTransactionType =
        creditLimitArray && creditLimitArray.type === UPGRADE_PER_TRANSACTION
          ? "txn"
          : "day";
      const debitLimitTransactionType =
        debitLimitArray && debitLimitArray.type === UPGRADE_PER_TRANSACTION
          ? "txn"
          : "day";

      const getCreditLimit = () => {
        if (creditLimitArray) {
          return !creditLimitArray.isUnlimited
            ? creditLimitArray.limit
            : UNLIMITED;
        }
        return props.creditTransactionLimit;
      };

      const getDebitLimit = () => {
        if (debitLimitArray) {
          return !debitLimitArray.isUnlimited
            ? debitLimitArray.limit
            : UNLIMITED;
        }
        return props.debitTransactionLimit;
      };

      const creditLimit = getCreditLimit();
      const debitLimit = getDebitLimit();

      const getWalletBalance = () => {
        const walletBalance = props.walletBalanceLimit as LimitsInfoLimitDTO;
        return !walletBalance?.isUnlimited ? walletBalance?.limit : UNLIMITED;
      };

      const get2FA = () => {
        if (Array.isArray(props.debitTransactionLimit)) {
          const hasLimit2FA = props.debitTransactionLimit.find(
            (item: LimitsInfoLimitDTO) => item.has2faLimit === true
          );

          return hasLimit2FA?.has2faLimit
            ? "/" +
                getSuffixedNumber(Number(hasLimit2FA?.limit2FA), 0, 0) +
                " (2FA)"
            : "";
        }
      };

      function handleRenderUpgradeButton() {
        if (selected) {
          return (
            <div className="mt-10 flex flex-row items-center justify-center px-4 py-1.5">
              <p>
                {" "}
                <OkTick fill="currentColor" className="mr-1.5 h-6 w-6" />{" "}
              </p>
              <p className="text-sm font-medium text-neutral-600">
                {" "}
                Current Account
              </p>
            </div>
          );
        }

        if (isTierLowerThanTargetTier) {
          return (
            <div className="mt-16 flex flex-row items-center justify-center px-4 py-1.5" />
          );
        }

        if (
          upgradeProcessTierName === tier &&
          upgradeProcessStatus ===
            StartUpgradeTierResponseDTO.generalProcessStatus.PENDING
        ) {
          return (
            <ButtonComponent
              id="upgrade-under-approval-button"
              label="Upgrade under Approval"
              fullWidth
              disabled
              size="large"
              type="secondary"
              className="mt-10"
              iconStart={
                <Clock
                  stroke="currentColor"
                  className="float-left flex h-6 w-6"
                />
              }
            />
          );
        }

        if (
          upgradeProcessTierName === tier &&
          upgradeProcessStatus ===
            StartUpgradeTierResponseDTO.generalProcessStatus.REJECTED
        ) {
          return (
            <ButtonComponent
              id="review-upgrade-button"
              label="Review Upgrade"
              fullWidth
              onClick={onClick}
              size="large"
              type="secondary"
              className="mt-10"
              iconStart={<EditIcon stroke="#da291c" />}
            />
          );
        }

        return (
          <ButtonComponent
            id={`upgrade-to-${tier?.toLowerCase()}-button`}
            label={`Upgrade to ${
              tier?.charAt(0)?.toUpperCase() + tier?.slice(1).toLowerCase()
            }`}
            fullWidth
            onClick={onClick}
            disabled={disableUpgrade}
            size="large"
            type="primary"
            className="mt-10"
          />
        );
      }

      return (
        <div
          className={`flex h-auto w-full flex-col justify-end rounded-xl ${
            selected
              ? "border-2 border-neutral-300 bg-white hover:shadow-2xl "
              : isTierLowerThanTargetTier
              ? "border border-solid border-neutral-100 opacity-60"
              : "border border-solid border-neutral-100 hover:shadow-2xl "
          }`}
        >
          <div
            className={`${
              selected
                ? "m-[1px] h-full rounded-xl border-2 border-primary-300 p-6"
                : " p-6"
            }`}
          >
            <div className="mb-auto">
              <p className="mb-4">{getTierIcon(tier)}</p>
              <h2
                className={`mb-0.5 text-2xl font-medium capitalize ${
                  isTierLowerThanTargetTier ? "opacity-60" : "text-neutral-600"
                }`}
              >
                {tier.toLowerCase()}
              </h2>
              <h3
                className={`${
                  isTierLowerThanTargetTier ? "opacity-60" : "text-neutral-600"
                } my-2`}
              >
                {subTitle}
              </h3>
            </div>

            <div className="flex-row">
              <div className="flex">
                <div className="flex w-28 flex-1 flex-col">
                  <p
                    className={`mb-1 text-xs ${
                      isTierLowerThanTargetTier
                        ? "opacity-60"
                        : "text-neutral-400"
                    }`}
                  >
                    Credit Limit ({creditLimitTransactionType})
                  </p>
                  <p
                    className={`${
                      isTierLowerThanTargetTier
                        ? "opacity-60"
                        : "text-neutral-600"
                    }`}
                  >
                    {creditLimit === UNLIMITED ? "" : DEFAULT_CURRENCY_SYMBOL}{" "}
                    {`${
                      creditLimit === UNLIMITED
                        ? UNLIMITED
                        : getSuffixedNumber(Number(creditLimit), 0, 0)
                    }`}
                  </p>
                </div>

                <div className="flex-2 flex w-119 flex-col">
                  <p
                    className={`mb-1 text-xs ${
                      isTierLowerThanTargetTier
                        ? "opacity-60"
                        : "text-neutral-400"
                    }`}
                  >
                    Debit Limit ({debitLimitTransactionType})
                  </p>{" "}
                  <p
                    className={`${
                      isTierLowerThanTargetTier
                        ? "opacity-60"
                        : "text-neutral-600"
                    }`}
                  >
                    {debitLimit === UNLIMITED ? "" : DEFAULT_CURRENCY_SYMBOL}{" "}
                    {`${
                      debitLimit === UNLIMITED
                        ? UNLIMITED
                        : getSuffixedNumber(Number(debitLimit), 0, 0)
                    }`}
                    {props.tierName ===
                      StartUpgradeTierRequestDTO.tierType.GOLD ||
                    props.tierName ===
                      StartUpgradeTierRequestDTO.tierType.PLATINUM
                      ? get2FA()
                      : ""}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex">
                <div className="flex w-28 flex-1 flex-col">
                  <p
                    className={`mb-1 text-xs ${
                      isTierLowerThanTargetTier
                        ? "opacity-60"
                        : "text-neutral-400"
                    }`}
                  >
                    Balance Limit
                  </p>
                  <p className={`mb-1 text-xs  text-neutral-400`}></p>
                  <p
                    className={` "text-neutral-600
                    `}
                  >
                    {getWalletBalance() === UNLIMITED
                      ? ""
                      : DEFAULT_CURRENCY_SYMBOL}{" "}
                    {`${
                      getWalletBalance() === UNLIMITED
                        ? UNLIMITED
                        : getSuffixedNumber(Number(getWalletBalance()), 0, 0)
                    }`}
                  </p>
                </div>

                <div className="flex-2 flex w-28 flex-col"></div>
              </div>
            </div>

            <div
              className={`my-4 border border-solid ${
                selected
                  ? "border-primary-300"
                  : isTierLowerThanTargetTier
                  ? "opacity-60"
                  : "border-neutral-300"
              } `}
              style={{ borderWidth: "0.5px" }}
            ></div>

            <p
              className={`text-xs ${
                isTierLowerThanTargetTier ? "opacity-60" : "text-neutral-600 "
              }`}
            >
              {description}
            </p>
            {handleRenderUpgradeButton()}
          </div>
        </div>
      );
    }
  );

export default CardTierComponent;
